import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchTrialInvites, inviteTrial } from "@/lib/platform-api";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
}

const EMPTY_FORM = {
  ownerEmail: "",
  orgName: "",
  ownerName: "",
  plan: "studio",
  trialDays: 14,
  notes: "",
};

export default function PlatformTrialInvites() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTrialInvites();
      setInvites(data.invites || []);
    } catch (err) {
      toast.error("Could not load trial invites", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await inviteTrial(form);
      setLastResult(result);
      if (result.emailSent) {
        toast.success("Trial invite sent", { description: result.ownerEmail });
      } else {
        toast.success("Trial workspace created", {
          description: result.temporaryPassword
            ? "Email not sent — share credentials manually."
            : result.tenantId,
        });
      }
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error("Invite failed", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1100px] mx-auto">
      <div className="text-overline">Platform</div>
      <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Trial invites</h1>
      <p className="text-sm text-[var(--text-secondary)] mt-3 max-w-2xl">
        Invite a prospect by email. We provision a 14-day Studio trial workspace with a sandbox Demo Event
        they can explore without affecting live KPIs.
      </p>

      <form onSubmit={submit} className="surface p-6 mt-8 space-y-4 max-w-xl">
        <div className="text-overline">Invite by email</div>
        <input
          className="input-dark w-full"
          placeholder="Email address *"
          type="email"
          required
          value={form.ownerEmail}
          onChange={(e) => setForm((p) => ({ ...p, ownerEmail: e.target.value }))}
          data-testid="trial-invite-email"
        />
        <input
          className="input-dark w-full"
          placeholder="Organization / workspace name"
          value={form.orgName}
          onChange={(e) => setForm((p) => ({ ...p, orgName: e.target.value }))}
        />
        <input
          className="input-dark w-full"
          placeholder="Contact name"
          value={form.ownerName}
          onChange={(e) => setForm((p) => ({ ...p, ownerName: e.target.value }))}
        />
        <div className="flex gap-3 flex-wrap items-center">
          <select className="input-dark" value={form.plan} onChange={(e) => setForm((p) => ({ ...p, plan: e.target.value }))}>
            <option value="studio">Studio (trial)</option>
            <option value="starter">Starter</option>
            <option value="scale">Scale</option>
          </select>
          <label className="text-xs text-[var(--text-secondary)] flex items-center gap-2">
            Trial days
            <input
              type="number"
              min={1}
              max={90}
              className="input-dark w-20"
              value={form.trialDays}
              onChange={(e) => setForm((p) => ({ ...p, trialDays: Number(e.target.value) }))}
            />
          </label>
        </div>
        <textarea
          className="input-dark w-full min-h-[72px]"
          placeholder="Internal notes (optional)"
          value={form.notes}
          onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
        />
        <button type="submit" className="btn-primary" disabled={busy} data-testid="trial-invite-submit">
          {busy ? "Sending invite…" : "Send 14-day trial invite"}
        </button>
      </form>

      {lastResult?.temporaryPassword && !lastResult?.emailSent ? (
        <div className="surface p-5 mt-6 border border-[var(--primary)]/40 max-w-xl">
          <div className="text-overline text-[var(--primary)]">Credentials (email not sent)</div>
          <p className="text-sm mt-2 font-mono break-all">{lastResult.temporaryPassword}</p>
          <p className="text-xs text-[var(--text-tertiary)] mt-2">{lastResult.loginUrl}</p>
        </div>
      ) : null}

      <div className="mt-12">
        <div className="text-overline mb-3">Recent trial workspaces</div>
        {loading ? (
          <p className="text-sm text-[var(--text-tertiary)]">Loading…</p>
        ) : (
          <div className="border border-[var(--border-default)] overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[var(--bg-elev)] text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                <tr>
                  <th className="text-left p-3">Workspace</th>
                  <th className="text-left p-3">Owner</th>
                  <th className="text-left p-3">Trial ends</th>
                  <th className="text-left p-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((row) => (
                  <tr key={row.id} className="border-t border-[var(--border-default)]">
                    <td className="p-3 font-medium">{row.name || row.id}</td>
                    <td className="p-3 font-mono text-xs">{row.ownerEmail || "—"}</td>
                    <td className="p-3 text-xs">{formatDate(row.trialEndsAt)}</td>
                    <td className="p-3 text-xs text-[var(--text-tertiary)]">{formatDate(row.createdAt)}</td>
                  </tr>
                ))}
                {!invites.length ? (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-[var(--text-tertiary)]">No trial workspaces yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
